import {
    Card,
    Text,
    Box,
    BlockStack,
    InlineStack,
    Badge,
    Button,
    TextField,
    DataTable,
    EmptyState,
    ProgressBar,
} from '@shopify/polaris';
import {
    CheckCircleIcon,
    PlusIcon,
    DeleteIcon,
} from '@shopify/polaris-icons';
import { CommissionOverride } from './Modals/CommissionOverride';
import apiClient from '../utils/api';
import { useState } from 'react';

export const CommissionSettings = ({
    saving,
    setSaving,
    isSyncing,
    defaultRate,
    setDefaultRate,
    updateDefaultCommission,
    brand,
    availableMarketplaceTags,
    availableMarketplacesLoading,
    setError,
    fetchBrandData,
}) => {
    const [showCommissionModal, setShowCommissionModal] = useState(false);

    const deleteCommissionOverride = async (overrideId) => {
        if (!confirm('Are you sure you want to delete this commission override?')) return;

        try {
            setSaving(true);
            await apiClient.deleteCommissionOverride(brand.id, overrideId);
            await fetchBrandData(); // Refresh data after delete
        } catch (err) {
            setError(err.message || 'Failed to delete commission override');
        } finally {
            setSaving(false);
        }
    };
    return (
        <>
            <BlockStack gap="600">
                {/* Default Commission Rate */}
                <Card>
                    <Box padding="600">
                        <BlockStack gap="500">
                            <BlockStack gap="200" align="start">
                                <Text variant="headingMd" as="h3">Default Commission Rate</Text>
                                <Text variant="bodyMd" tone="subdued">
                                    Base commission rate applied to all orders unless overridden by marketplace-specific rules
                                </Text>
                            </BlockStack>

                            <InlineStack gap="400" align="start" blockAlign="center">
                                <Box minWidth="200px">
                                    <TextField
                                        label="Commission Rate"
                                        type="number"
                                        value={defaultRate}
                                        onChange={setDefaultRate}
                                        suffix="%"
                                        min="0"
                                        max="100"
                                        step="0.1"
                                        helpText="Enter rate as percentage (e.g., 30 for 30%)"
                                    />
                                </Box>
                                <Button
                                    variant="primary"
                                    onClick={updateDefaultCommission}
                                    loading={saving}
                                    disabled={saving || isSyncing}
                                    icon={CheckCircleIcon}
                                    size="large"
                                >
                                    Update Rate
                                </Button>
                            </InlineStack>

                            {saving && <ProgressBar progress={undefined} size="small" />}
                        </BlockStack>
                    </Box>
                </Card>

                {/* Commission Overrides */}
                <Card>
                    <Box padding="600">
                        <BlockStack gap="500">
                            <InlineStack align="space-between" blockAlign="center">
                                <BlockStack gap="200">
                                    <Text variant="headingMd" as="h3">Marketplace Commission Overrides</Text>
                                    <Text variant="bodyMd" tone="subdued">
                                        Set different commission rates for specific marketplace tags (e.g., Target, Amazon, Macy's)
                                    </Text>
                                </BlockStack>
                                <Button
                                    variant="primary"
                                    icon={PlusIcon}
                                    onClick={() => setShowCommissionModal(true)}
                                >
                                    Add Override
                                </Button>
                            </InlineStack>

                            {brand.commissionOverrides?.length > 0 ? (
                                <DataTable
                                    columnContentTypes={['text', 'numeric', 'text', 'text']}
                                    headings={['Marketplace Tag', 'Commission Rate', 'Created', 'Actions']}
                                    rows={brand.commissionOverrides.map(override => [
                                        <Badge tone="info">{override.orderTag}</Badge>,
                                        `${(override.commissionRate * 100).toFixed(1)}%`,
                                        (() => {
                                            const date = new Date(override.createdAt || Date.now());
                                            return isNaN(date.getTime()) ? 'Unknown' : date.toLocaleDateString();
                                        })(),
                                        <Button
                                            variant="tertiary"
                                            tone="critical"
                                            size="micro"
                                            icon={DeleteIcon}
                                            onClick={() => deleteCommissionOverride(override.id)}
                                            loading={saving}
                                        />
                                    ])}
                                />
                            ) : (
                                <EmptyState
                                    heading="No commission overrides configured"
                                    content="Add marketplace-specific commission rates to override the default rate for orders with specific tags."
                                    action={{
                                        content: 'Add Override',
                                        onAction: () => setShowCommissionModal(true)
                                    }}
                                />
                            )}
                        </BlockStack>
                    </Box>
                </Card>
            </BlockStack>
            <CommissionOverride
                brand={brand}
                availableMarketplaceTags={availableMarketplaceTags}
                availableMarketplacesLoading={availableMarketplacesLoading}
                saving={saving}
                showCommissionModal={showCommissionModal}
                setShowCommissionModal={setShowCommissionModal}
                setSaving={setSaving}
                setError={setError}
                fetchBrandData={fetchBrandData}
            />
        </>
    )
}